/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import useVersionMovil from "../../hooks/useMobileVersion";
import "../../styles/PersonasTable.css";

/**
 * Componente principal para búsqueda y visualización de personal.
 * 
 * Sistema completo de búsqueda de personas con:
 * - Formulario de búsqueda avanzada con múltiples criterios
 * - Vista de resultados adaptada a escritorio y móvil
 * - Vista detallada de persona seleccionada
 * - Gestión de estados de búsqueda y resultados
 * - Integración con Microsoft Entra para autenticación
 */
function PersonasTable() {

  /* ============================
     Estado de búsqueda y datos
     ============================ */
  
  // Resultados de la búsqueda
  const [personas, setPersonas] = useState([]);

  // Persona seleccionada para ver detalle
  const [selectedPersona, setSelectedPersona] = useState(null);

  // Campos de búsqueda
  const [nombreInput, setNombreInput] = useState("");
  const [apellido1Input, setApellido1Input] = useState("");
  const [apellido2Input, setApellido2Input] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [telefonoInput, setTelefonoInput] = useState("");
  const [extensionInput, setExtensionInput] = useState("");
  const [cargoInput, setCargoInput] = useState("");
  const [departamentoInput, setDepartamentoInput] = useState("");
  const [edificioInput, setEdificioInput] = useState("");
  // Lista de departamentos para el select desplegable
  const [departamentos, setDepartamentos] = useState([]);
  const [edificios, setEdificios] = useState([]);

  // Estado de la interfaz
  const isMobile = useVersionMovil();
  const { instance, accounts } = useMsal();
  const [showResults, setShowResults] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");


  /* ============================
     Funciones de búsqueda
     ============================ */
  
  /**
   * Realiza la búsqueda de personas según los criterios establecidos.
   * Valida que haya al menos un campo relleno y gestiona la respuesta.
   */
  const fetchPersonas = async () => {
    // Validación: al menos un campo debe tener contenido
    if (
      !nombreInput.trim() &&
      !apellido1Input.trim() &&
      !apellido2Input.trim() &&
      !emailInput.trim() &&
      !telefonoInput.trim() &&
      !extensionInput.trim() &&
      !cargoInput.trim() &&
      !departamentoInput.trim() &&
      !edificioInput.trim()
    ) {
      setAlertMessage("Por favor, rellene al menos un campo para buscar.");
      setPersonas([]);
      setShowResults(true);
      return;
    }

    setAlertMessage("");

    try {
      // Obtener token de acceso
      const tokenResponse = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });
      const token = tokenResponse.accessToken;

      // Llamada a la API con parámetros de búsqueda
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/personas`, {
        params: {
          nombre: nombreInput.trim(),
          apellido1: apellido1Input.trim(),
          apellido2: apellido2Input.trim(),
          email: emailInput.trim(),
          telefono: telefonoInput.trim(),
          extension: extensionInput.trim(),
          cargo: cargoInput.trim(),
          departamento: departamentoInput.trim(),
          edificio: edificioInput.trim(),
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Procesar respuesta
      if (response.data.message) {
        setAlertMessage(response.data.message);
        setPersonas([]);
       } else {
        // Resultados de la búsqueda
        const results = response.data.content || [];
      
	const dataOrdenada = results.sort((a, b) =>
          a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
        );

	setPersonas(results);

        // Si solo hay un resultado, mostrar directamente su detalle
        if (results.length === 1) {
          setSelectedPersona(results[0]);
        } else {
          setSelectedPersona(null);
        }
      }

      setShowResults(true);


      setNombreInput("");
      setApellido1Input("");
      setApellido2Input("");
      setEmailInput("");
      setTelefonoInput("");
      setExtensionInput("");
      setCargoInput("");
      setDepartamentoInput("");
      setEdificioInput("");

    } catch (error) {
      console.error("Error fetching personas:", error);
      setAlertMessage("Error buscando personas en el servidor.");
      setPersonas([]);
      setShowResults(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // evita recargar la página
    fetchPersonas();
  };

  /* ============================
     Efectos
     ============================ */
  
  /**
   * Carga inicial de departamentos para el select.
   * Se ejecuta cuando el usuario está autenticado.
   */
  useEffect(() => {
    const fetchDepartamentos = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [process.env.REACT_APP_API_SCOPE],
        });

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/departamentos`,
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
          }
        );

        const data = Array.isArray(res.data) ? res.data : res.data.content || [];
      
        const dataOrdenada = data.sort((a, b) =>
          a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
        );

        setDepartamentos(data);
      } catch (error) {
        console.error("Error cargando departamentos", error);
      }
    };

    if (accounts.length > 0) {
      fetchDepartamentos();
    }
  }, [accounts, instance]);

  /**
   * Carga inicial de edificios para el select.
   * Se ejecuta cuando el usuario está autenticado.
   */
  useEffect(() => {
    const fetchEdificios = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [process.env.REACT_APP_API_SCOPE],
        });

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/edificios`,
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
          }
        );

        const data = Array.isArray(res.data) ? res.data : res.data.content || [];
      
        const dataOrdenada = data.sort((a, b) =>
          a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
        );

        setEdificios(data);

      } catch (error) {
        console.error("Error cargando edificios", error);
      }
    };

    if (accounts.length > 0) {
      fetchEdificios();
    }
  }, [accounts, instance]);

  /**
   * Restablece todos los campos de búsqueda y resultados.
   * Vuelve al estado inicial del formulario.
   */
  const handleReset = () => {
    setNombreInput("");
    setApellido1Input("");
    setApellido2Input("");
    setEmailInput("");
    setTelefonoInput("");
    setExtensionInput("");
    setCargoInput("");
    setDepartamentoInput("");
    setEdificioInput("");
    setPersonas([]);
    setSelectedPersona(null);
    setShowResults(false);
    setAlertMessage("");
  };


  /*************************************************************
   *               Renderizado del componente                  *
   *************************************************************/
  return (
    <div className="container mt-1">
      <div className="row h-100">

        {/* =============================================
            FORMULARIO DE BÚSQUEDA (Escritorio)
            ============================================= */}
        {(!isMobile || !showResults) && (
          <div className="col-md-6">
            <div className="card shadow h-100">
              <form className="card-body d-flex flex-column" onSubmit={handleSubmit}>
                {/* Título con línea decorativa */}
                <div className={isMobile ? "text-center mb-2" : "text-center mb-4"}>
                  <h5 className="card-title fw-semibold mb-2">Búsqueda de Personal</h5>
                  <div
                    style={{
                      width: "220px",
                      height: "3px",
                      backgroundColor: "#0d6efd",
                      margin: "0 auto",
                      borderRadius: "2px"
                    }}
                  />
                </div>

                {/* Campos del formulario */}
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nombre"
                    value={nombreInput}
                    onChange={(e) => setNombreInput(e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Primer apellido"
                    value={apellido1Input}
                    onChange={(e) => setApellido1Input(e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Segundo apellido"
                    value={apellido2Input}
                    onChange={(e) => setApellido2Input(e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Correo electrónico"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    maxLength={100}
                  />
                </div>
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Teléfono"
                    value={telefonoInput}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // elimina no números
                      setTelefonoInput(value);
                    }}
                    maxLength={9}
                    inputMode="numeric"
                    pattern="[0-9]*"
  
                  />
                </div>
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Extensión"
                    value={extensionInput}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // elimina no números
                      setExtensionInput(value);
                    }}
                    maxLength={4}
                    inputMode="numeric"
                    pattern="[0-9]*"
  
                  />
                </div>
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Cargo"
                    value={cargoInput}
                    onChange={(e) => setCargoInput(e.target.value)}
                    maxLength={250}
                  />
                </div>

                {/* Select de departamentos */}
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <select
                    className="form-select"
                    value={departamentoInput}
                    onChange={(e) => setDepartamentoInput(e.target.value)}
                  >
                    <option value="">Selecciona un departamento</option>
                    {departamentos.map((d) => (
                      <option key={d.id} value={d.nombre}>
                        {d.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              
                {/* Select de edificios */}
                <div className={isMobile ? "mb-2" : "mb-3"}>
                  <select
                    className="form-select"
                    value={edificioInput}
                    onChange={(e) => setEdificioInput(e.target.value)}
                  >
                    <option value="">Selecciona un edificio</option>
                    {edificios.map((e) => (
                      <option key={e.id} value={e.nombre}>
                        {e.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Botones de acción */}
                <div className="mt-auto d-flex gap-2">
                  <button type="submit" className="btn btn-primary">
                    Buscar
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleReset}>
                    Reset
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===================================================
            PANEL DERECHO: Instrucciones / Resultados / Detalle
            =================================================== */}
        {(!isMobile || showResults) && (
          <div className={isMobile ? "col-12" : "col-md-6"}>
            <div className="card shadow ms-2 me-2 h-100">

            <div className="card-body d-flex flex-column h-100">

              {/* Instrucciones de uso (solo escritorio) */}
              {!showResults && !isMobile && (
                <>
                  <h5 className="card-title mb-3">Instrucciones de uso</h5>
                  <ol>
                    <li>Rellena uno o varios campos de búsqueda.</li>
                    <li>Pulsa <strong>Buscar</strong> para mostrar los resultados a la derecha.</li>
                    <li>Haz clic en cualquier resultado para ver todos los datos de esa persona.</li>
                    <li>Pulsa <strong>Reset</strong> para volver a las instrucciones.</li>
                  </ol>
                </>
              )}

               {/* =============================
                    VISTA DETALLE DE PERSONA
                    ============================= */}
              {showResults && selectedPersona && (
                <div className="d-flex flex-column align-items-center">

                  {/* Nombre completo */}
                  <h5 className="mb-3 text-center">
                    <strong>
                      {selectedPersona.nombre} {selectedPersona.apellido1} {selectedPersona.apellido2}
                    </strong>
                  </h5>

                  {/* Foto de perfil */}
                  <img
                    src={
                      selectedPersona.fotoUrl
                        ? `${process.env.REACT_APP_URL}${selectedPersona.fotoUrl}`
                        : `${process.env.REACT_APP_URL}/uploads/default.png`
                    }
                    alt="Foto de la persona"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "50%",
                      marginBottom: "1rem",
                      boxShadow: "4px 4px 12px rgba(0,0,0,0.2)",
                    }}
                  />

                  {/* Información detallada */}
                  <ul className="list-group w-100 text-start">
                    <li className="list-group-item"><strong>Departamento:</strong> {selectedPersona.departamento?.nombre ?? "-"}</li>
                    <li className="list-group-item"><strong>Cargo:</strong> {selectedPersona.cargo ?? "-"}</li>
                    <li className="list-group-item"><strong>Email:</strong> {selectedPersona.email}</li>
                    <li className="list-group-item"><strong>Teléfono:</strong> {selectedPersona.telefono ?? "-"}</li>
                    <li className="list-group-item"><strong>Extensión:</strong> {selectedPersona.extension ?? "-"}</li>
                    <li className="list-group-item"><strong>Edificio:</strong> {selectedPersona.edificio?.nombre ?? "-"}</li>   
                  </ul>

                  {/* Botones de navegación */}
                  {personas.length > 1 && (
                    <button
                      className="btn btn-primary mt-3"
                      onClick={() => setSelectedPersona(null)}
                    >
		      <i className="bi bi-arrow-left me-2"></i>
                      Volver a resultados
                    </button>
                  )}
                  {personas.length === 1 && (
                    <button
                      className="btn btn-primary mt-3"
                      onClick={handleReset}
                    >
		      <i className="bi bi-arrow-left me-2"></i>
                      Volver al formulario
                    </button>
                  )}
                </div>
                
              
              )}
              

              {/* =============================
                    LISTA DE RESULTADOS
                  ============================= */}
              {showResults && !selectedPersona && (
                isMobile ? (
                
                /* ======== VERSIÓN MÓVIL ======== */
                <div
                  className="d-flex flex-column"
                  style={{
                    height: "65vh",      // Altura fija para el móvil
                    overflow: "hidden"   // Control de scroll interno
                  }}
                >
                    {/* CABECERA */}
                    <div className="flex-shrink-0 mb-2">
                      <h5 className="card-title mb-0">Resultados</h5>
                    </div>

                    {/* LISTADO CON SCROLL INTERNO*/}
                    <div className="flex-grow-1 overflow-auto" 
                      style={{
                        overflow: "hidden",
                        border: "1px solid #ddd",
                        borderRadius: "0.5rem",
                        padding: "0.5rem",
                        background: "rgba(185, 185, 185, 0.2)"
                      }}
                    >
                      {alertMessage ? (
                        <div className="alert alert-warning">{alertMessage}</div>
                      ) : personas.length === 0 ? (
                        <div className="alert alert-warning">
                          No se encontraron resultados.
                        </div>
                      ) : (
                        <div className="list-group">
                          {personas.map((p) => (
                            <button
                              key={p.id}
                              className="list-group-item list-group-item-action d-flex flex-column justify-content-center align-items-center text-center shadow mb-1"
                              style={{
                                borderRadius: "12px",
                                padding: "12px"
                              }}
                              onClick={() => setSelectedPersona(p)}
                            >
                              {/* Nombre y apellidos en negrita */}
                              <span className="fw-bold">
                                {p.nombre} {p.apellido1} {p.apellido2}
                              </span>

                              {/* Departamento debajo, más pequeño y sin negrita */}
                              <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                                {p.departamento?.nombre || ""}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* PIE CON BOTÓN DE VOLVER */}
                    <div className="flex-shrink-0 mt-2">
                      <button
                        className="btn btn-primary w-100"
                        onClick={handleReset}
                      >
			<i className="bi bi-arrow-left me-2"></i>
                        Volver al formulario
                      </button>
                    </div>

                  </div>
                ) : (
                  /* ======== VERSIÓN DE ESCRITORIO ======== */
                  <>
                    <h5 className="card-title mb-2">Resultados</h5>
                    <div className="card" style={{ height: "55vh", background: "rgba(128,128,128,0.1)" }}>
                      <div className="card-body flex-grow-1 overflow-auto" style={{background: "rgba(128,128,128,0.1)"}}>
                        {alertMessage ? (
                          <div className="alert alert-warning">{alertMessage}</div>
                        ) : personas.length === 0 ? (
                          <div className="alert alert-warning">No se encontraron resultados.</div>
                        ) : (
                          <div className="list-group">
                            {personas.map((p) => (
                              <button
                                key={p.id}
                                className="list-group-item list-group-item-action d-flex flex-column justify-content-center align-items-center text-center shadow mb-2"
                                style={{
                                  borderRadius: "12px",
                                  padding: "12px"
                                }}
                                onClick={() => setSelectedPersona(p)}
                              >
                                {/* Nombre y apellidos en negrita */}
                                <span className="fw-bold">
                                  {p.nombre} {p.apellido1} {p.apellido2}
                                </span>

                                {/* Departamento debajo, más pequeño y sin negrita */}
                                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                                  {p.departamento?.nombre || ""}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )
              )}

            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export default PersonasTable;
